import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Menu,
    X,
    Phone,
    MapPin,
    Clock,
    Facebook,
    Instagram,
    Youtube,
    MessageCircle,
    Languages,
    ChevronRight,
    ArrowUp
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTenantTheme } from './TenantThemeContext';
import { useLenis } from './ui/primitives';
import { getSectionOrder } from './templates';

/**
 * TenantLayout — premium navbar + footer for tenant public pages.
 * Reads palette/template from TenantThemeContext and renders bilingual chrome.
 */
export default function TenantLayout({ children }) {
    const { tenant, template, sectionEnabled } = useTenantTheme();
    const { language, toggleLanguage } = useLanguage();
    const { garageSlug } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const [counts, setCounts] = useState({ services: null, parts: null });

    useLenis(template.motionIntensity !== 'low' && template.motionIntensity !== 'none');

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Lightweight count fetches so we can hide nav items when there is nothing
    // to show. We only fetch once per slug — endpoints are cached publicly.
    useEffect(() => {
        if (!garageSlug) return;
        let cancelled = false;
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        Promise.all([
            axios.get(`${API}/tenants/${garageSlug}/services`).then(r => Array.isArray(r.data) ? r.data.length : (r.data?.services?.length || 0)).catch(() => 0),
            axios.get(`${API}/tenants/${garageSlug}/parts`).then(r => Array.isArray(r.data) ? r.data.length : (r.data?.parts?.length || 0)).catch(() => 0)
        ]).then(([s, p]) => {
            if (!cancelled) setCounts({ services: s, parts: p });
        });
        return () => { cancelled = true; };
    }, [garageSlug]);

    const base = `/${garageSlug}`;
    const reviewsCount = tenant?.reviews_summary?.count || 0;

    const allNavItems = useMemo(() => ([
        { id: 'home', orderKey: 'hero', to: base, label: 'Home', labelBn: 'হোম', exact: true, show: true },
        { id: 'services', orderKey: 'services', to: `${base}/services`, sectionId: 'services', label: 'Services', labelBn: 'সার্ভিস', show: counts.services === null || counts.services > 0 },
        { id: 'store', orderKey: 'parts', to: `${base}/store`, sectionId: 'store', label: 'Store', labelBn: 'পার্টস', show: counts.parts === null || counts.parts > 0 },
        { id: 'booking', orderKey: 'booking', to: `${base}#booking`, hash: 'booking', label: 'Book', labelBn: 'বুকিং', show: sectionEnabled('booking') },
        { id: 'tracker', orderKey: 'jobtracker', to: `${base}#tracker`, hash: 'tracker', label: 'Track Job', labelBn: 'ট্র্যাকিং', show: sectionEnabled('job_tracker') },
        { id: 'reviews', orderKey: 'reviews', to: `${base}#reviews`, hash: 'reviews', label: 'Reviews', labelBn: 'রিভিউ', show: reviewsCount > 0 && sectionEnabled('reviews') }
    ]), [base, counts.services, counts.parts, sectionEnabled, reviewsCount]);

    // Sort visible nav items by the active template's section order so the
    // navbar mirrors the on-page narrative (Forest vs Express, etc.).
    const navItems = useMemo(() => {
        const order = getSectionOrder(template?.id);
        const visible = allNavItems.filter(n => n.show);
        return visible.sort((a, b) => {
            // Home always first.
            if (a.id === 'home') return -1;
            if (b.id === 'home') return 1;
            const ai = order.indexOf(a.orderKey);
            const bi = order.indexOf(b.orderKey);
            return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });
    }, [allNavItems, template?.id]);

    // Scroll-spy: track which section is currently in view on the home page.
    // We watch ALL nav-mapped section IDs (incl. services/store previews) so the
    // top nav lights up the matching tab even though those tabs link out to
    // dedicated pages. Sections like Reviews / RealWorkReel mount async after
    // their data fetch — we watch the DOM with a MutationObserver and rebind
    // the IntersectionObserver as soon as new IDs appear.
    useEffect(() => {
        const onHome = location.pathname === base || location.pathname === `${base}/`;
        if (!onHome) {
            setActiveSection('');
            return;
        }
        const ids = ['services', 'store', 'booking', 'tracker', 'reviews'];
        let observer = null;
        let observed = new Set();

        const handle = (entries) => {
            const visible = entries
                .filter(e => e.isIntersecting)
                .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
            if (visible[0]) setActiveSection(visible[0].target.id);
        };

        const bind = () => {
            if (!observer) {
                observer = new IntersectionObserver(handle, {
                    rootMargin: '-35% 0px -55% 0px',
                    threshold: [0, 0.25, 0.5, 0.75, 1]
                });
            }
            ids.forEach(id => {
                if (observed.has(id)) return;
                const el = document.getElementById(id);
                if (el) {
                    observer.observe(el);
                    observed.add(id);
                }
            });
        };

        bind();
        // Re-bind whenever new sections (e.g. Reviews after async fetch) mount.
        const mo = new MutationObserver(() => bind());
        mo.observe(document.body, { childList: true, subtree: true });

        return () => {
            mo.disconnect();
            if (observer) observer.disconnect();
            observed = new Set();
        };
    }, [location.pathname, base]);

    const isActive = useCallback((item) => {
        const onHome = location.pathname === base || location.pathname === `${base}/`;
        if (item.hash) {
            return onHome && activeSection === item.hash;
        }
        if (item.exact) {
            return onHome && !activeSection;
        }
        // Sub-route tab (Services / Store): active when on the dedicated page,
        // OR when the matching preview section is in view on the home page.
        if (location.pathname.startsWith(item.to.split('#')[0]) && !onHome) return true;
        if (onHome && item.sectionId && activeSection === item.sectionId) return true;
        return false;
    }, [location.pathname, activeSection, base]);

    const handleNavClick = useCallback((item) => (e) => {
        const onHome = location.pathname === base || location.pathname === `${base}/`;
        // Home click: when already on home, scroll to top; otherwise let it navigate.
        if (item.exact) {
            if (onHome) {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                window.history.replaceState(null, '', base);
                setActiveSection('');
                setOpen(false);
            }
            return;
        }
        if (!item.hash) return;
        const el = onHome ? document.getElementById(item.hash) : null;
        if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            window.history.replaceState(null, '', `${base}#${item.hash}`);
            setActiveSection(item.hash);
            setOpen(false);
        } else if (!onHome) {
            e.preventDefault();
            navigate(`${base}#${item.hash}`);
            setOpen(false);
        }
    }, [base, location.pathname, navigate]);

    // When landing on /:slug with a #hash, scroll smoothly after mount.
    useEffect(() => {
        const onHome = location.pathname === base || location.pathname === `${base}/`;
        if (!onHome || !location.hash) return;
        const id = location.hash.replace('#', '');
        const tryScroll = () => {
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setActiveSection(id);
            }
        };
        // Defer until sections likely mounted.
        const t = setTimeout(tryScroll, 250);
        return () => clearTimeout(t);
    }, [location.pathname, location.hash, base]);

    const phone = tenant?.phone;
    const whatsappHref = phone
        ? `https://wa.me/${phone.replace(/[^\d]/g, '')}?text=${encodeURIComponent('Hi, I would like to book a service.')}`
        : null;

    const name = (language === 'bn' && tenant?.name_bn) ? tenant.name_bn : tenant?.name;
    const monogram = (tenant?.name || 'G').trim().charAt(0).toUpperCase();

    function handleLogoClick(e) {
        // If already on the home page (no hash, no sub-path), scroll to top instead of nav.
        const onHome = location.pathname === base || location.pathname === `${base}/`;
        if (onHome) {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    return (
        <div className="min-h-screen bg-[var(--tenant-surface)] text-[var(--tenant-ink)] font-sans selection:bg-[var(--tenant-primary)] selection:text-[var(--tenant-surface)]">
            {/* Navigation */}
            <header
                className={[
                    'fixed top-0 inset-x-0 z-50 transition-all duration-300',
                    scrolled
                        ? 'bg-[var(--tenant-surface)]/85 backdrop-blur-xl border-b border-[var(--tenant-border)]'
                        : 'bg-transparent'
                ].join(' ')}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 h-16 md:h-20 flex items-center justify-between gap-2 sm:gap-4 md:gap-6">
                    <Link to={base} onClick={handleLogoClick} className="flex items-center gap-2 sm:gap-3 group min-w-0 flex-shrink">
                        {tenant?.logo_url ? (
                            <img
                                src={tenant.logo_url}
                                alt={`${tenant?.name || 'Garage'} logo`}
                                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border border-[var(--tenant-border)] flex-shrink-0"
                            />
                        ) : (
                            <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full inline-flex items-center justify-center bg-[var(--tenant-primary)] text-[var(--tenant-surface)] font-headline font-extrabold text-base sm:text-lg flex-shrink-0">
                                {monogram}
                            </span>
                        )}
                        <span className={`${language === 'bn' ? 'font-bn' : 'font-headline font-extrabold tracking-tight'} truncate min-w-0`} style={{ fontSize: language === 'bn' ? 16 : 18 }}>
                            <span className="text-[var(--tenant-ink)]">{name || 'Garage'}</span>
                        </span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1">
                        {navItems.map(item => {
                            const active = isActive(item);
                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    onClick={handleNavClick(item)}
                                    aria-current={active ? 'page' : undefined}
                                    className={[
                                        'relative px-3 py-2 text-sm tracking-wide uppercase transition-colors',
                                        active
                                            ? 'text-[var(--tenant-primary)]'
                                            : 'text-[var(--tenant-ink)]/80 hover:text-[var(--tenant-primary)]'
                                    ].join(' ')}
                                >
                                    {language === 'bn' ? <span className="font-bn normal-case text-[15px]">{item.labelBn}</span> : item.label}
                                    <span
                                        className={[
                                            'pointer-events-none absolute left-3 right-3 -bottom-0.5 h-[2px] rounded-full bg-[var(--tenant-primary)] origin-left transition-transform duration-300',
                                            active ? 'scale-x-100' : 'scale-x-0'
                                        ].join(' ')}
                                    />
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                        <button
                            type="button"
                            onClick={toggleLanguage}
                            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-9 sm:h-10 rounded-full text-[11px] sm:text-xs uppercase tracking-wider border border-[var(--tenant-border)] text-[var(--tenant-ink)] hover:bg-[var(--tenant-surface-alt)] transition-colors"
                        >
                            <Languages className="w-3.5 h-3.5" />
                            {language === 'bn' ? 'EN' : 'বাং'}
                        </button>
                        {phone && (
                            <a
                                href={`tel:${phone}`}
                                aria-label={phone}
                                className="inline-flex items-center gap-2 h-9 sm:h-10 px-3 sm:px-4 rounded-full bg-[var(--tenant-primary)] text-[var(--tenant-surface)] text-sm font-semibold hover:brightness-110 transition"
                            >
                                <Phone className="w-4 h-4" />
                                <span className="hidden md:inline">{phone}</span>
                            </a>
                        )}
                        <button
                            type="button"
                            aria-label="Menu"
                            onClick={() => setOpen(v => !v)}
                            className="lg:hidden inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-[var(--tenant-border)]"
                        >
                            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {/* Mobile drawer */}
                {open && (
                    <div className="lg:hidden border-t border-[var(--tenant-border)] bg-[var(--tenant-surface)]/95 backdrop-blur-xl">
                        <nav className="flex flex-col py-2">
                            {navItems.map(item => {
                                const active = isActive(item);
                                return (
                                    <Link
                                        key={item.to}
                                        to={item.to}
                                        onClick={handleNavClick(item)}
                                        className={[
                                            'flex items-center justify-between px-5 py-3 text-base border-b border-[var(--tenant-border)]/40 hover:bg-[var(--tenant-surface-alt)]',
                                            active ? 'text-[var(--tenant-primary)]' : ''
                                        ].join(' ')}
                                    >
                                        <span className={language === 'bn' ? 'font-bn' : ''}>
                                            {language === 'bn' ? item.labelBn : item.label}
                                        </span>
                                        <ChevronRight className="w-4 h-4 opacity-50" />
                                    </Link>
                                );
                            })}
                            <button
                                type="button"
                                onClick={toggleLanguage}
                                className="flex items-center justify-between px-5 py-3 text-base"
                            >
                                <span className="inline-flex items-center gap-2">
                                    <Languages className="w-4 h-4" />
                                    {language === 'bn' ? 'English' : 'বাংলা'}
                                </span>
                                <ChevronRight className="w-4 h-4 opacity-50" />
                            </button>
                        </nav>
                    </div>
                )}
            </header>

            <main className="pt-16 md:pt-20">
                {children}
            </main>

            {/* WhatsApp floating button */}
            {whatsappHref && (
                <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Chat on WhatsApp"
                    className="fixed bottom-5 right-5 z-40 inline-flex items-center justify-center w-14 h-14 rounded-full shadow-xl bg-[#25D366] text-white hover:scale-105 transition-transform"
                >
                    <MessageCircle className="w-6 h-6" fill="currentColor" />
                </a>
            )}

            {/* Powered by Motopotti — floating badge (desktop expands on hover, mobile shows compact dot+M) */}
            <a
                href="https://motopotti.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Powered by Motopotti"
                className={[
                    'powered-by-badge group fixed left-5 z-40 inline-flex items-center gap-2 rounded-full',
                    'bg-[var(--tenant-surface)]/85 backdrop-blur-md border border-[var(--tenant-border)]',
                    'text-[10px] uppercase tracking-[0.28em] text-[var(--tenant-muted)]',
                    'hover:text-[var(--tenant-ink)] hover:border-[var(--tenant-primary)]/40 shadow-sm',
                    'overflow-hidden whitespace-nowrap',
                    'h-9 pl-2 pr-2 md:pr-2 md:hover:pr-3',
                    // Desktop: compact circle that expands on hover; Mobile: always compact circle
                    'md:max-w-[44px] md:hover:max-w-[260px]',
                    'max-w-[44px]',
                    scrolled ? 'bottom-5 opacity-100' : '-bottom-12 opacity-0 pointer-events-none'
                ].join(' ')}
                style={{ transition: 'max-width 420ms cubic-bezier(0.22,1,0.36,1), bottom 400ms cubic-bezier(0.22,1,0.36,1), opacity 300ms ease' }}
            >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--tenant-primary)] text-[var(--tenant-surface)] font-headline font-extrabold text-[11px] shrink-0">
                    M
                </span>
                <span className="opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 text-[10px]">
                    Powered by
                </span>
                <span className="opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 font-headline font-extrabold tracking-tight text-[var(--tenant-ink)] normal-case text-xs">
                    MotoPotti
                </span>
            </a>

            {/* Scroll-to-top */}
            {scrolled && (
                <button
                    type="button"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    aria-label="Back to top"
                    className="fixed bottom-24 right-5 z-40 inline-flex items-center justify-center w-11 h-11 rounded-full bg-[var(--tenant-surface)] border border-[var(--tenant-border)] text-[var(--tenant-ink)] hover:border-[var(--tenant-primary)]/50 hover:text-[var(--tenant-primary)] shadow-md transition-all"
                >
                    <ArrowUp className="w-4 h-4" />
                </button>
            )}

            {/* Footer — dark themed gradient (ink → primary blend) so text stays
                fully legible regardless of palette. We use --tenant-ink as the
                dominant tone and tint with primary for brand presence. Text
                switches to --tenant-surface (light) for high contrast. */}
            <footer
                className="mt-32 border-t border-[var(--tenant-border)] relative overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, var(--tenant-ink) 0%, color-mix(in srgb, var(--tenant-ink) 70%, var(--tenant-primary) 30%) 55%, color-mix(in srgb, var(--tenant-ink) 35%, var(--tenant-primary) 65%) 100%)',
                    color: 'var(--tenant-surface)'
                }}
            >
                <div
                    className="pointer-events-none absolute inset-0 opacity-[0.07]"
                    style={{
                        background: 'radial-gradient(800px 400px at 80% 0%, var(--tenant-primary), transparent 60%), radial-gradient(600px 300px at 0% 100%, var(--tenant-accent, var(--tenant-primary)), transparent 60%)'
                    }}
                />
                <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 grid md:grid-cols-4 gap-10">
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-3 mb-4">
                            {tenant?.logo_url ? (
                                <img src={tenant.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                                <span className="w-10 h-10 rounded-full inline-flex items-center justify-center bg-[var(--tenant-primary)] text-[var(--tenant-surface)] font-headline font-extrabold">
                                    {monogram}
                                </span>
                            )}
                            <span className="font-headline font-extrabold text-2xl tracking-tight" style={{ color: 'var(--tenant-surface)' }}>{tenant?.name || 'Garage'}</span>
                        </div>
                        {tenant?.tagline && (
                            <p className="max-w-md" style={{ color: 'color-mix(in srgb, var(--tenant-surface) 75%, transparent)' }}>
                                {language === 'bn' && tenant.tagline_bn ? tenant.tagline_bn : tenant.tagline}
                            </p>
                        )}
                        {tenant?.social_links && (
                            <div className="flex items-center gap-3 mt-6">
                                {tenant.social_links.facebook && (
                                    <a href={tenant.social_links.facebook} aria-label="Facebook" className="w-10 h-10 rounded-full inline-flex items-center justify-center transition-colors" style={{ border: '1px solid color-mix(in srgb, var(--tenant-surface) 25%, transparent)', color: 'var(--tenant-surface)' }}><Facebook className="w-4 h-4" /></a>
                                )}
                                {tenant.social_links.instagram && (
                                    <a href={tenant.social_links.instagram} aria-label="Instagram" className="w-10 h-10 rounded-full inline-flex items-center justify-center transition-colors" style={{ border: '1px solid color-mix(in srgb, var(--tenant-surface) 25%, transparent)', color: 'var(--tenant-surface)' }}><Instagram className="w-4 h-4" /></a>
                                )}
                                {tenant.social_links.youtube && (
                                    <a href={tenant.social_links.youtube} aria-label="YouTube" className="w-10 h-10 rounded-full inline-flex items-center justify-center transition-colors" style={{ border: '1px solid color-mix(in srgb, var(--tenant-surface) 25%, transparent)', color: 'var(--tenant-surface)' }}><Youtube className="w-4 h-4" /></a>
                                )}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.32em] mb-4" style={{ color: 'color-mix(in srgb, var(--tenant-primary) 65%, var(--tenant-surface) 35%)' }}>Visit us</div>
                        {tenant?.address && (
                            <address className="not-italic flex items-start gap-2 mb-3" style={{ color: 'color-mix(in srgb, var(--tenant-surface) 75%, transparent)' }}>
                                <MapPin className="w-4 h-4 mt-1 shrink-0" style={{ color: 'color-mix(in srgb, var(--tenant-primary) 60%, var(--tenant-surface) 40%)' }} />
                                <span>{language === 'bn' && tenant.address_bn ? tenant.address_bn : tenant.address}</span>
                            </address>
                        )}
                        {phone && (
                            <a href={`tel:${phone}`} className="flex items-center gap-2 hover:opacity-100 transition-opacity" style={{ color: 'color-mix(in srgb, var(--tenant-surface) 80%, transparent)' }}>
                                <Phone className="w-4 h-4" /> {phone}
                            </a>
                        )}
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.32em] mb-4" style={{ color: 'color-mix(in srgb, var(--tenant-primary) 65%, var(--tenant-surface) 35%)' }}>Hours</div>
                        {(tenant?.opening_hours || []).slice(0, 7).map(h => (
                            <div key={h.day} className="flex items-center justify-between text-sm py-1" style={{ color: 'color-mix(in srgb, var(--tenant-surface) 75%, transparent)' }}>
                                <span>{h.day}</span>
                                <span>{h.closed ? '—' : `${h.open}–${h.close}`}</span>
                            </div>
                        ))}
                        {(!tenant?.opening_hours || tenant.opening_hours.length === 0) && (
                            <div className="flex items-center gap-2 text-sm" style={{ color: 'color-mix(in srgb, var(--tenant-surface) 75%, transparent)' }}>
                                <Clock className="w-4 h-4" /> Contact us for hours
                            </div>
                        )}
                    </div>
                </div>
                <div
                    className="relative py-5 text-center text-xs"
                    style={{
                        borderTop: '1px solid color-mix(in srgb, var(--tenant-surface) 15%, transparent)',
                        color: 'color-mix(in srgb, var(--tenant-surface) 65%, transparent)'
                    }}
                >
                    © {new Date().getFullYear()} {tenant?.name || 'Garage'} · Powered by{' '}
                    <a href="https://motopotti.com" className="hover:underline" style={{ color: 'color-mix(in srgb, var(--tenant-primary) 70%, var(--tenant-surface) 30%)' }}>MotoPotti</a>
                </div>
            </footer>
        </div>
    );
}
